// =============================================================
//  AuthLayout — Mise en page split-screen pour les écrans d'auth
//  (Login, Register, ForgotPassword). Inspiré du design Amiral,
//  adapté à React Native Web et au branding MyConcours.
//
//  - Côté gauche (desktop) : bandeau bleu marine avec logo +
//    titre marketing + footer copyright. Caché sur mobile.
//  - Côté droit : zone blanche avec le formulaire (max 420px).
// =============================================================

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { colors, spacing, radius } from '../../admin/web-ui/theme';

export default function AuthLayout({
  title,
  subtitle,
  tagline = 'Trouve tes concours,\nréussis tes études.',
  description = "MyConcours centralise tous les sujets d'épreuves, fiches de cours et ressources dont tu as besoin pour préparer tes concours.",
  children,
  footer,
}) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  return (
    <View style={styles.root}>
      {/* Côté gauche : branding (caché sur mobile) */}
      {isDesktop && (
        <View style={styles.brandPane}>
          <View style={styles.brandHeader}>
            <Image
              source={require('../../assets/logo.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.brandName}>MyConcours</Text>
          </View>

          <View style={styles.brandBody}>
            <Text style={styles.brandTitle}>{tagline}</Text>
            <Text style={styles.brandText}>{description}</Text>
          </View>

          <Text style={styles.brandFooter}>
            © {new Date().getFullYear()} MyConcours. Made in Cameroon.
          </Text>
        </View>
      )}

      {/* Côté droit : formulaire */}
      <ScrollView
        style={styles.formPane}
        contentContainerStyle={styles.formScroll}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.formInner}>
          {/* Logo mobile (visible quand <900px) */}
          {!isDesktop && (
            <View style={styles.mobileHeader}>
              <View style={styles.mobileLogoWrap}>
                <Image
                  source={require('../../assets/logo.png')}
                  style={styles.mobileLogo}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.mobileBrand}>MyConcours</Text>
            </View>
          )}

          {!!title && <Text style={styles.title}>{title}</Text>}
          {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

          <View style={{ marginTop: spacing.lg }}>{children}</View>

          {!!footer && <View style={{ marginTop: spacing.xl }}>{footer}</View>}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
  },

  // ----- Côté gauche : branding -----
  brandPane: {
    flex: 1,
    backgroundColor: colors.sidebarBg,
    paddingHorizontal: 48,
    paddingVertical: 48,
    justifyContent: 'space-between',
  },
  brandHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#fff',
    padding: 4,
  },
  brandName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  brandBody: {
    maxWidth: 460,
  },
  brandTitle: {
    color: '#fff',
    fontSize: 36,
    fontWeight: '700',
    lineHeight: 44,
    letterSpacing: -0.5,
    marginBottom: 16,
  },
  brandText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 15,
    lineHeight: 22,
  },
  brandFooter: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 12,
  },

  // ----- Côté droit : formulaire -----
  formPane: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  formScroll: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 48,
  },
  formInner: {
    width: '100%',
    maxWidth: 420,
  },

  // Logo en haut sur mobile uniquement
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 36,
  },
  mobileLogoWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.sidebarBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileLogo: {
    width: 30,
    height: 30,
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: 2,
  },
  mobileBrand: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '700',
  },

  title: {
    fontSize: 26,
    fontWeight: '700',
    color: colors.text,
    letterSpacing: -0.4,
  },
  subtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 8,
    lineHeight: 20,
  },
});
